import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import type { MfaOtpChannel } from '@interfaces/IMfa';
import { copyToClipboard } from '@utils/clipboard';
import { useTransientFlag } from '@hooks/useTransientFlag';
import styles from './styles.module.css';

export interface IFormActionsProps {
  onCancel: () => void;
  submitLabel: string;
  submitDisabled: boolean;
  busy: boolean;
  variant?: 'primary' | 'danger';
}

/**
 * The cancel/submit pair every MFA sub-form ends with. One component because the markup was
 * identical in each of them — and because a sub-form that puts its buttons in a different order,
 * or forgets to disable cancel while a request is in flight, should have to say so explicitly.
 */
export function FormActions(props: IFormActionsProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.formActions}>
      <Button variant="ghost" size="md" onClick={props.onCancel} disabled={props.busy}>
        {t('common.cancel')}
      </Button>
      <Button
        type="submit"
        variant={props.variant ?? 'primary'}
        size="md"
        disabled={props.submitDisabled}
      >
        {props.submitLabel}
      </Button>
    </div>
  );
}

export interface IBackupCodesPanelProps {
  title: string;
  body: string;
  /** aria-label for the codes list (the two flows label it differently). */
  codesLabel: string;
  codes: string[];
  onDone: () => void;
}

/**
 * One-time backup codes behind a save gate — shown after a first-factor email
 * enrolment and after a regeneration (where the OLD set is already dead).
 * Copy/save state lives here so leaving the view resets it via unmount.
 */
export function BackupCodesPanel({ title, body, codesLabel, codes, onDone }: IBackupCodesPanelProps) {
  const { t } = useTranslation();
  const [copied, flashCopied] = useTransientFlag();
  const [saved, setSaved] = useState(false);

  return (
    <div className={styles.subForm}>
      <div>
        <h3 className={styles.subTitle}>{title}</h3>
        <p className={styles.subBody}>{body}</p>
      </div>
      <ul className={styles.codeGrid} aria-label={codesLabel}>
        {codes.map((c) => (
          <li key={c} className={styles.backupCode}>
            {c}
          </li>
        ))}
      </ul>
      <div className={styles.backupActions}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void copyToClipboard(codes.join('\n')).then((ok) => ok && flashCopied(true))}
        >
          {copied ? t('auth.mfa.enroll.copied') : t('auth.mfa.enroll.copyCodes')}
        </Button>
      </div>
      <label className={styles.gate}>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>{t('auth.mfa.enroll.savedConfirm')}</span>
      </label>
      <div className={styles.formActions}>
        <Button variant="primary" size="md" disabled={!saved} onClick={onDone}>
          {t('auth.mfa.settings.saveCodesDone')}
        </Button>
      </div>
    </div>
  );
}

export interface IPasswordGateFormProps {
  title: string;
  body: string;
  submitLabel: string;
  submittingLabel: string;
  submitVariant: 'primary' | 'danger';
  password: string;
  onPasswordChange: (next: string) => void;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

/**
 * The account-password gate shared by the remove-factor and regenerate-codes
 * flows — the same form with a different consequence behind the submit.
 */
export function PasswordGateForm({
  title,
  body,
  submitLabel,
  submittingLabel,
  submitVariant,
  password,
  onPasswordChange,
  submitting,
  onCancel,
  onSubmit,
}: IPasswordGateFormProps) {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className={styles.subForm} noValidate>
      <div>
        <h3 className={styles.subTitle}>{title}</h3>
        <p className={styles.subBody}>{body}</p>
      </div>
      <Field
        label={t('auth.mfa.settings.passwordLabel')}
        type="password"
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
        autoComplete="current-password"
        autoFocus
        required
      />
      <div className={styles.formActions}>
        <Button variant="ghost" size="md" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant={submitVariant} size="md" disabled={submitting || password.length === 0}>
          {submitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}

export interface IPrimaryChannelRowProps {
  /** The channels the user has actually enrolled, in the order the backend listed them. */
  channels: MfaOtpChannel[];
  /** The current default; `undefined` while none is recorded (a user who predates the field). */
  value: MfaOtpChannel | undefined;
  onChange: (channel: MfaOtpChannel) => void | Promise<void>;
  busy?: boolean;
}

/**
 * Which enrolled channel a sign-in challenge uses by default.
 *
 * Rendered only when the user has MORE THAN ONE channel — with a single channel there is no
 * decision to make, and a control that cannot change anything is just noise. The challenge
 * screen can still override this per sign-in; this is only the starting point, which is what
 * matters when the user is somewhere their phone has no signal.
 */
export function PrimaryChannelRow({ channels, value, onChange, busy }: IPrimaryChannelRowProps) {
  const { t } = useTranslation();
  if (channels.length < 2) return null;
  return (
    <div className={styles.recoveryRow}>
      <div className={styles.methodInfo}>
        <span className={styles.methodLabel}>{t('auth.mfa.settings.primaryChannelTitle')}</span>
        <span className={styles.methodHint}>{t('auth.mfa.settings.primaryChannelBody')}</span>
      </div>
      <SegmentedControl<MfaOtpChannel>
        value={value ?? channels[0]!}
        onChange={(next) => {
          if (busy) return;
          void onChange(next);
        }}
        ariaLabel={t('auth.mfa.settings.primaryChannelTitle')}
        options={channels.map((c) => ({ id: c, label: t(`auth.mfa.channel.${c}`) }))}
      />
    </div>
  );
}
